<template>
  <div class="tinymce-editor-container">
    <div :id="editorId"></div>
  </div>
</template>

<script>
import emitter from 'element-ui/src/mixins/emitter'
import { isEn } from '@/i18n'
import tinymce from 'tinymce'
import 'tinymce/plugins/image'
import 'tinymce/plugins/imagetools'
import 'tinymce/plugins/link'
import 'tinymce/plugins/media'
import 'tinymce/plugins/table'
import 'tinymce/plugins/lists'
import 'tinymce/plugins/advlist'
import _ from 'lodash'
import { getFilePath, uploadFile } from '@/utils/qiniu'
import { base64SrcToBlob } from '@/utils/common'
import signDefaultImage from '@/assets/image/offerLetter/sign_default.png'
import { OFFER_LETTER_TEMPLATE_FIELD_TYPE } from '@/constant/company'

export default {
  mixins: [emitter],
  model: {
    prop: 'value',
    event: 'change'
  },
  props: {
    value: {
      type: String,
      required: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    templateFields: {
      type: Array
    },
    showTemplateField: {
      type: Boolean,
      default: false
    },
    currentAccountId: {
      required: true
    }
  },
  data () {
    const editorId = `instance_${new Date().valueOf()}_${parseInt(Math.random() * 1000 + 1)}`
    return {
      isEn,
      editorId,
      editor: null
    }
  },
  computed: {
    contentStyle () {
      const style = `
        body {
          margin: 0;
          padding: 96px 120px;
          span p div {
            font-family: "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", 微软雅黑, Arial, sans-serif;
          }
        }
      `
      return style
    }
  },
  created () {

  },
  mounted () {
    this.createEditor()
  },
  beforeDestroy () {
    this.editor = null
  },
  methods: {
    setup (editor) {
      editor.ui.registry.addMenuButton('placeholderButton', {
        text: this.$t('admin.company.offerLetterInviteRecordManagement.label.templateFieldKey'),
        fetch: (callback) => {
          const items = _.map(this.templateFields, templateField => {
            return {
              type: 'menuitem',
              text: templateField.i18nName,
              onAction: async () => {
                let templateFieldContent = '${' + templateField.key + '}'
                const valueType = templateField.value_type
                if (valueType === OFFER_LETTER_TEMPLATE_FIELD_TYPE.SIGN) {
                  const file = base64SrcToBlob(signDefaultImage)
                  const fileName = file.name || 'sign_default'
                  const fileKey = `offerLetter/template/${this.currentAccountId}/content/${fileName}`
                  const resp = await uploadFile(file, fileKey)
                  if (resp) {
                    this.fileKey = resp.key
                    const filePath = getFilePath(this.fileKey)
                    templateFieldContent = `<img src="${filePath}" id="offer-sign-image" alt="${templateFieldContent}" style="width:100px;height:40px;" width="100" height="40" />`
                  }
                }
                editor.insertContent(templateFieldContent)
              }
            }
          })
          callback(items)
        }
      })
    },
    async createEditor () {
      if (!this.editor) {
        const setting = {
          base_url: `${window.location.origin}/vue-static/tinymce`,
          suffix: '.min',
          min_width: 806,
          height: '100vh',
          font_formats: 'Andale Mono="andale mono", sans-serif; Arial=arial, sans-serif; Arial Black="arial black", sans-serif; Book Antiqua=BookAntiqua-Bold, sans-serif; Comic Sans MS="comic sans ms", sans-serif; Georgia=georgia, sans-serif; Helvetica=AGHelvetica, sans-serif; Impacted=impact, sans-serif; Tahoma=Tahoma, sans-serif; Terminal=PCapTerminal, sans-serif; Times New Roman="times new roman", times, serif; Trebuchet MS="trebuchet ms", sans-serif; Verdana=verdana, sans-serif; Webdings=webdings; Wingdings=WingdingsW95-1, sans-serif; 思源黑体=SourceHanSansSC-VF, sans-serif;',
          target: this.$el.firstElementChild,
          noneditable_noneditable_class: 'mceNonEditable',
          content_style: this.contentStyle,
          plugins: ['image', 'imagetools', 'link', 'media', 'table', 'lists', 'advlist', 'noneditable', 'paste'],
          toolbar: [
            'undo redo pastetext forecolor backcolor bold italic underline strikethrough link alignleft aligncenter alignright alignjustify bullist numlist image removeformat'
          ],
          setup: this.setup,
          extended_valid_elements: 'email-header, email-footer',
          custom_elements: 'email-header,email-footer',
          templates: this.templateFields,
          branding: false,
          resize: true,
          toolbar_mode: 'wrap',
          convert_urls: false,
          relative_urls: false,
          content_css: [
          ],
          language: isEn() ? 'en' : 'zh_CN',
          menubar: false,
          images_upload_handler: this.imagesUploadHandler,
          paste_preprocess: (plugin, args) => {
            const parser = new DOMParser()
            const doc = parser.parseFromString(args.content, 'text/html')
            // 设置默认字体
            Array.from(doc.body.getElementsByTagName('*')).forEach(node => {
              node.style.fontFamily = 'Times New Roman'
            })
            // 更新args.content为清理后的内容
            args.content = doc.body.innerHTML
          }
        }
        if (this.showTemplateField) {
          setting.plugins.push('template')
          setting.toolbar.push('formatselect fontselect fontsizeselect lineheight table placeholderButton')
        } else {
          setting.toolbar.push('formatselect fontselect fontsizeselect lineheight table')
        }
        const editors = await tinymce.init(setting)
        const editor = editors[0]
        this.editor = editor
        editor.on('change input undo redo', () => {
          const content = editor.getContent()
          this.$emit('change', content)
          this.dispatch('ElFormItem', 'el.form.change', [content])
        })
      }
      this.editor.setContent(this.value || '')
      this.editor.setMode(this.disabled ? 'readonly' : 'design')
    },
    async imagesUploadHandler (blobInfo, success, failure, progress) {
      const file = blobInfo.blob()
      const fileName = file.name
      const fileKey = `offerLetter/template/${this.currentAccountId}/content/${fileName}`
      const resp = await uploadFile(file, fileKey)
      if (resp) {
        this.fileKey = resp.key
        const filePath = getFilePath(this.fileKey)
        success(filePath)
      }
    }
  },
  watch: {
    '$i18n.locale' () {
      if (this.editor) {
        this.editor.settings.language = isEn() ? 'en' : 'zh_CN'
        this.editor.render()
      }
    },
    value: {
      immediate: true,
      handler () {
        const value = this.value || ''
        if (this.editor) {
          const content = this.editor.getContent()
          if (content !== value) {
            this.editor.setContent(value)
            this.editor.setMode(this.disabled ? 'readonly' : 'design')
          }
        }
      }
    },
    editor (newVal, oldVal) {
      if (oldVal) {
        tinymce.remove(oldVal)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@font-face {
  font-family: 'AGHelvetica';
  src: url('~@/assets/icomoon/fonts/AGHelvetica.ttf') format('truetype');
}
@font-face {
  font-family: 'BookAntiqua-Bold';
  src: url('~@/assets/icomoon/fonts/BookAntiqua-Bold.ttf') format('truetype');
}
@font-face {
  font-family: 'Tahoma';
  src: url('~@/assets/icomoon/fonts/Tahoma.ttf') format('truetype');
}
@font-face {
  font-family: 'PCapTerminal';
  src: url('~@/assets/icomoon/fonts/PCapTerminal.ttf') format('truetype');
}
@font-face {
  font-family: 'WingdingsW95-1';
  src: url('~@/assets/icomoon/fonts/WingdingsW95-1.ttf') format('truetype');
}
@font-face {
  font-family: 'SourceHanSansSC-VF';
  src: url('~@/assets/icomoon/fonts/SourceHanSansSC-VF.ttf') format('truetype');
}
</style>
